import { trackFeatureUsage } from "../utils/analytics";

/**
 * Service Desk: ciclo de vida del ticket.
 *
 * La pregunta que responde este modulo no es cuanta gente lo abre, sino donde
 * se atoran los tickets: que estado concentra el tiempo, cuantos se escalan y
 * si el SLA ya estaba roto cuando por fin alguien lo movio. Por eso cada
 * cambio de estado viaja con la prioridad y el estado de SLA del momento.
 *
 * Privacidad: nunca sale el titulo, la descripcion, la referencia de soporte,
 * el cuerpo del comentario, los nombres de los archivos adjuntos ni los datos
 * del solicitante. Viajan enumeraciones (estado, prioridad, SLA), banderas y
 * conteos.
 */

export type StatusChangeMethod = "kanban_drag" | "detail_select";

const FEATURE = "service_desk";

/**
 * Cambio de estado del ticket.
 *
 * `slaState` es la propiedad cara: permite ver cuantos movimientos ocurren
 * cuando el SLA ya esta en `breached`, o sea cuando el cliente ya se enojo.
 */
export const trackTicketStatusChanged = (params: {
  fromStatus: string;
  toStatus: string;
  priority: string;
  escalated: boolean;
  slaState: string;
  method: StatusChangeMethod;
}): void => {
  void trackFeatureUsage(FEATURE, "ticket_status_changed", {
    from_status: params.fromStatus,
    to_status: params.toStatus,
    priority: params.priority,
    escalated: params.escalated,
    sla_state: params.slaState,
    method: params.method,
  });
};

export const trackTicketStatusChangeFailed = (params: {
  toStatus: string;
  method: StatusChangeMethod;
}): void => {
  void trackFeatureUsage(FEATURE, "ticket_status_change_failed", {
    to_status: params.toStatus,
    method: params.method,
  });
};

/** Cambio de prioridad, con el valor anterior para medir reclasificaciones. */
export const trackTicketPriorityChanged = (
  fromPriority: string,
  toPriority: string,
): void => {
  void trackFeatureUsage(FEATURE, "ticket_priority_changed", {
    from_priority: fromPriority,
    to_priority: toPriority,
  });
};

export const trackTicketAssigned = (params: {
  target: "agent" | "dev";
  wasAssigned: boolean;
  priority: string;
  escalated: boolean;
}): void => {
  void trackFeatureUsage(FEATURE, "ticket_assigned", {
    assign_target: params.target,
    was_assigned: params.wasAssigned,
    priority: params.priority,
    escalated: params.escalated,
  });
};

/**
 * Escalamiento a desarrollo (o su reversion).
 *
 * Es el momento en que un ticket deja de ser soporte y pasa a costar tiempo
 * de ingenieria; separarlo del cambio de estado permite medirlo solo.
 */
export const trackTicketEscalationChanged = (params: {
  escalated: boolean;
  priority: string;
  status: string;
}): void => {
  void trackFeatureUsage(FEATURE, "ticket_escalation_changed", {
    escalated: params.escalated,
    priority: params.priority,
    status: params.status,
  });
};

/**
 * Respuesta en el hilo.
 *
 * `internal` separa la nota interna de la respuesta al cliente; el cuerpo
 * nunca viaja, solo si venia vacio y cuantos adjuntos trae.
 */
export const trackTicketReplied = (params: {
  internal: boolean;
  fileCount: number;
  hasBody: boolean;
}): void => {
  void trackFeatureUsage(FEATURE, "ticket_replied", {
    internal: params.internal,
    file_count: params.fileCount,
    has_body: params.hasBody,
  });
};

export const trackTicketDeleted = (params: {
  status: string;
  priority: string;
  escalated: boolean;
}): void => {
  void trackFeatureUsage(FEATURE, "ticket_deleted", {
    status: params.status,
    priority: params.priority,
    escalated: params.escalated,
  });
};

/**
 * Enlace con GitHub.
 *
 * `mode` distingue crear un issue nuevo de enlazar uno existente; nunca viaja
 * la URL ni el nombre del repositorio.
 */
export const trackTicketGithubLinked = (params: {
  mode: "create" | "link_existing";
  success: boolean;
}): void => {
  void trackFeatureUsage(FEATURE, "ticket_github_linked", {
    mode: params.mode,
    success: params.success,
  });
};
